import { isPercentageFeeType } from '@/components/Freight/shared'
import { Form, message } from 'antd'
import { useCallback } from 'react'
import { constructOverridePayload, constructRebillDetailPayload } from '../utils'
import type { TransactionDetail } from '../../columnDefs'
import type { BookType, InvoiceDetail, TransactionRow } from '@/api/useFreight/types'
import { useTransactions } from '@/api/useFreight/useTransactions'
import { calculatePercentageTotal } from '../utils'
import {
  type AccessorialContext,
  type FormActionType,
  FormActionTypes,
  type AccessorialFormType,
  type AccessorialOptionType,
  type BaseFreightFormType,
  type FLIFormType,
} from '../types'

const defaultContext = {
  counterpartyId: undefined,
  bookType: undefined,
  rebillUpdateActions: undefined,
}

export function useAddFreightLineItemForm<T extends AccessorialFormType | BaseFreightFormType>(
  bookType: BookType,
  actionType: FormActionType,
  order?: TransactionRow,
  fliDetail?: InvoiceDetail | TransactionDetail,
  context: AccessorialContext = defaultContext
) {
  const [form] = Form.useForm<T>()

  const { useOverrideTransactionMutation } = useTransactions()

  const handleFLIOverride = useCallback(
    async (values: T) => {
      const payload = constructOverridePayload(fliDetail?.id || '', fliDetail?.customer_id || '', bookType, values)
      await useOverrideTransactionMutation.mutateAsync(payload)
      message.success('Line item overridden successfully.')
    },
    [fliDetail?.id, bookType, useOverrideTransactionMutation]
  )

  const handleCreditRebillFlow = useCallback(
    async (
      values: T,
      formType: FLIFormType,
      selectedAccessorial?: AccessorialOptionType | TransactionDetail | InvoiceDetail,
      productGroup?: string | null
    ) => {
      const units = values.uom || selectedAccessorial?.feeType || selectedAccessorial?.uom
      const isPercentageBased = isPercentageFeeType(units || '')
      const amount = values.amount ?? 0
      const finalAmount = isPercentageBased ? 1 : amount
      const finalRate = values.rate ?? selectedAccessorial?.rate ?? 0
      const calculatedTotal = calculatePercentageTotal(units || '', finalRate, order?.details)
      const finalTotal = isPercentageBased ? (values.total ?? calculatedTotal ?? 0) : amount * finalRate

      if (actionType === FormActionTypes.create && context.rebillUpdateActions?.addRebillLineItem) {
        const rebillDetail = constructRebillDetailPayload(
          formType,
          selectedAccessorial || { label: values.base_freight_subtype, feeType: units, requiresApproval: false },
          order || ({} as TransactionRow),
          values,
          productGroup,
          finalRate,
          finalAmount,
          finalTotal,
          bookType
        )
        context.rebillUpdateActions.addRebillLineItem(
          rebillDetail as unknown as Omit<TransactionDetail | InvoiceDetail, 'id'>,
          order?.order_id || ''
        )
        message.success('Rebill Line item added successfully.')
      } else if (actionType === FormActionTypes.edit && context.rebillUpdateActions?.overrideRebillLineItem) {
        context.rebillUpdateActions.overrideRebillLineItem(fliDetail as InvoiceDetail, productGroup, finalTotal, values)
      }
    },
    [context.rebillUpdateActions, fliDetail]
  )

  function resetForm() {
    form.resetFields()
  }

  return {
    form,
    isOverrideMutationPending: useOverrideTransactionMutation.isPending,
    handleFLIOverride,
    handleCreditRebillFlow,
    resetForm,
  }
}
